import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ConciergeChat from "@/components/ConciergeChat";
import Hero from "@/components/home/Hero";
import Reassurance from "@/components/home/Reassurance";
import TestimonialsMarquee from "@/components/home/TestimonialsMarquee";
import StickyCta from "@/components/home/StickyCta";
import Accompagnements from "@/components/home/Accompagnements";
import DestinationsCarousel from "@/components/home/DestinationsCarousel";
import CreerMonVoyage from "@/components/home/CreerMonVoyage";
import CarnetsShowcase from "@/components/home/CarnetsShowcase";
import { Reveal } from "@/components/home/Reveal";

const FALLBACK_DESTINATIONS = [
  { url: "https://images.unsplash.com/photo-1776876648949-63ccabf63b10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbCUyMHJvb20lMjBvY2VhbiUyMHZpZXd8ZW58MHx8fHwxNzgwNTc0NTg5fDA&ixlib=rb-4.1.0&q=85", name: "Maldives", subtitle: "Lagons turquoise" },
  { url: "https://images.pexels.com/photos/7974839/pexels-photo-7974839.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", name: "Marrakech", subtitle: "Riads d'exception" },
  { url: "https://images.pexels.com/photos/33389169/pexels-photo-33389169.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", name: "Côte d'Azur", subtitle: "Suites face mer" },
];

export default function Home() {
  const [showcase, setShowcase] = useState(null);
  useEffect(() => {
    api.get("/public/showcase").then((r) => setShowcase(r.data)).catch(() => setShowcase([]));
  }, []);

  // Prefill the Concierge when arriving from a profile CTA or a public carnet
  // (e.g. /?voyage=famille&dest=Bali#devis or /?dest=Polynésie#devis)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const voyage = params.get("voyage");
    const dest = params.get("dest");
    if (!voyage && !dest) return;
    const base = {
      noces: "Je prépare un voyage de noces",
      couple: "Je souhaite organiser un voyage en couple",
      famille: "Je souhaite organiser un voyage en famille",
      amis: "Nous partons entre amis, en groupe",
      senior: "Je prépare un voyage confortable pour des voyageurs seniors",
      grands: "Je rêve d'un grand voyage sur plusieurs destinations",
      solo: "Je voyage en solo",
      luxe: "Je souhaite un voyage haut de gamme, très confortable",
      budget: "Je cherche un voyage au meilleur budget",
      aventure: "Je veux un voyage aventure, actif et hors des sentiers battus",
      "bien-etre": "Je cherche un séjour bien-être et détente",
      nomade: "Je prévois un séjour longue durée",
    }[voyage];
    let seed;
    if (base) {
      seed = dest ? `${base} — destination souhaitée : ${dest}.` : `${base}.`;
    } else if (dest) {
      seed = `J'aimerais un voyage sur-mesure inspiré de ce carnet — destination souhaitée : ${dest}.`;
    }
    if (!seed) return;
    const t = setTimeout(() => window.dispatchEvent(new CustomEvent("dt:concierge-open", { detail: { seed } })), 800);
    return () => clearTimeout(t);
  }, []);

  const cards = (showcase && showcase.length)
    ? showcase.map((s) => ({ key: s.id, to: s.url, img: s.cover_image, title: s.title, subtitle: s.subtitle }))
    : FALLBACK_DESTINATIONS.map((f) => ({ key: f.name, to: null, img: f.url, title: f.name, subtitle: f.subtitle }));

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1B18] pb-20 md:pb-0">
      <Helmet>
        <title>DealTravel | Voyages sur mesure et devis</title>
        <meta name="description" content="DealTravel — Maison de voyage sur-mesure. Couples, familles, lunes de miel et grands voyages composés par un Travel Planner dédié. Devis personnalisé gratuit et sans engagement." />
        <link rel="canonical" href="https://dealtravel.eu/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DealTravel" />
        <meta property="og:title" content="DealTravel | Voyages sur mesure et devis" />
        <meta property="og:description" content="Maison de voyage sur-mesure : couples, familles, lunes de miel et grands voyages composés par un Travel Planner dédié." />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Nav overHero />

      <Hero />
      <Reassurance />

      {/* NOS EXPÉRIENCES DE VOYAGE */}
      <Accompagnements />

      {/* COMMENCER LA CONVERSATION — Concierge + formulaire */}
      <CreerMonVoyage />

      {/* DESTINATIONS EN LUMIÈRE + SEO */}
      <section id="destinations" className="py-24 px-6 md:px-12 bg-white border-t border-[#EAE8E3]">
        <div className="max-w-[1400px] mx-auto">
          <Reveal className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <p className="label-eyebrow">Destinations en lumière</p>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-4">Là où nous vous emmenons.</h2>
            </div>
            <Link to="/destinations" data-testid="destinations-cta" className="text-sm uppercase tracking-[0.24em] hover:text-[#96836B] transition-colors">
              Voir toutes les destinations →
            </Link>
          </Reveal>
          <DestinationsCarousel cards={cards} />
        </div>
      </section>

      {/* LES CARNETS DE VOYAGE (masqué si vide) */}
      <CarnetsShowcase />

      {/* TÉMOIGNAGES — dernière preuve sociale avant le footer */}
      <TestimonialsMarquee />

      <Footer />
      <StickyCta />
      <ConciergeChat />
    </div>
  );
}
